import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, KeyRound, ShieldAlert } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(token ? "" : "This link is missing its code — open it from your email again.");

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { setErr("Use at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Those passwords don't match."); return; }
    setBusy(true); setErr("");
    try {
      const res = await api.post("/auth/password/set", { token, password: pw });
      setUser(res.data);
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(d && typeof d === "object" ? d.message : errMsg(e2, "Couldn't set your password."));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-sand-50 flex items-center justify-center p-6">
      <form onSubmit={submit} className="max-w-md w-full bg-white rounded-2xl border border-sand-200 p-8" data-testid="set-password-page">
        <div className="mx-auto h-12 w-12 rounded-2xl bg-redline-50 flex items-center justify-center mb-4">
          <KeyRound className="h-6 w-6 text-redline-500" strokeWidth={1.75} />
        </div>
        <h1 className="font-heading font-bold text-xl text-ink text-center">Create your password</h1>
        <p className="text-sm text-muted-foreground mt-2 text-center">Pick something you'll remember. From now on you sign in with your email and this password — on any device.</p>
        <input data-testid="set-password-input" type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (8+ characters)"
          className="mt-6 w-full rounded-full bg-white border border-sand-200 px-4 py-3 text-sm outline-none focus:border-ink" />
        <input data-testid="set-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Type it again"
          className="mt-3 w-full rounded-full bg-white border border-sand-200 px-4 py-3 text-sm outline-none focus:border-ink" />
        {err && <p className="mt-3 text-sm text-danger-700 flex items-start gap-1.5" data-testid="set-password-error"><ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> {err}</p>}
        <button data-testid="set-password-submit" type="submit" disabled={busy || !token}
          className="mt-5 w-full rounded-full bg-ink text-white font-semibold py-3 text-sm hover:bg-ink/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : "Save password and sign in"}
        </button>
        <Link to="/login" className="block text-center mt-4 text-sm font-semibold text-redline-600">Back to sign in</Link>
      </form>
    </div>
  );
}
